import { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { Button } from "@/components/ui/button";
import ThinkingPulse from "@/components/ThinkingPulse";
import { MessageSquare, Send, Loader2, Sparkles, X, Crown, Lock, Cloud, HardDrive, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { ChatMessage } from "./ChatMessage";
import { parseActionsFromResponse, cleanContentFromActions, parseNavigationFromResponse } from "./parseActions";
import type { Msg, ActionProposal, GraftonChatProps } from "./types";
import PaywallDialog from "../PaywallDialog";
import { probeLocalOllama } from "@/services/llmRouter";
import { useAuth } from "@/contexts/AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const LS_DASHBOARD_MODE = "grafton_dashboard_chat_mode_v1";
const LS_DASHBOARD_OLLAMA_URL = "grafton_dashboard_ollama_url_v1";
const LS_DASHBOARD_OLLAMA_MODEL = "grafton_dashboard_ollama_model_v1";

const DEFAULT_QUICK_PROMPTS = [
  "Comment ameliorer mon CA ce mois-ci ?",
  "Analyse ma tresorerie",
  "Propose un plan d'action commercial",
  "Redige un email de relance client",
];

export const GraftonChatPanel = ({ context, quickPrompts, subtitle, welcomeMessage, onNavigate, variant }: GraftonChatProps) => {
  const QUICK_PROMPTS = quickPrompts || DEFAULT_QUICK_PROMPTS;
  const chatSubtitle = subtitle || "Conseiller strategique";
  const chatWelcome = welcomeMessage || "Posez-moi une question sur vos KPIs, votre strategie ou vos operations.";
  const isModule = !!variant && variant !== "generic";
  const isDashboard = variant === "dashboard";

  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Msg[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [actionsMap, setActionsMap] = useState<Record<number, ActionProposal[]>>({});
  const [sessionId, setSessionId] = useState<string | null>(() => {
    try {
      return localStorage.getItem(isModule ? `grafton_${variant}_chat_session_id` : "grafton_chat_session_id");
    } catch {
      return null;
    }
  });
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const { user: authUser } = useAuth();
  const isFounder = authUser?.email === "broulou.joel@me.com";
  const [actionCount, setActionCount] = useState(0);
  const [maxFreeActions, setMaxFreeActions] = useState(10);
  const [showPaywall, setShowPaywall] = useState(false);

  // Dashboard mode: Cloud/Local toggle
  const [llmMode, setLlmMode] = useState<"cloud" | "local">(() => {
    try { return (localStorage.getItem(LS_DASHBOARD_MODE) as "cloud" | "local") || "cloud"; } catch { return "cloud"; }
  });
  const [ollamaUrl, setOllamaUrl] = useState<string>(() => {
    try { return localStorage.getItem(LS_DASHBOARD_OLLAMA_URL) || "http://localhost:11434"; } catch { return "http://localhost:11434"; }
  });
  const [ollamaModel, setOllamaModel] = useState<string>(() => {
    try { return localStorage.getItem(LS_DASHBOARD_OLLAMA_MODEL) || "gemma2:9b"; } catch { return "gemma2:9b"; }
  });
  const [localProbe, setLocalProbe] = useState<{ reachable: boolean; models?: string[] } | null>(null);

  // Persist module prefs
  useEffect(() => {
    if (!isModule) return;
    try { localStorage.setItem(LS_DASHBOARD_MODE, llmMode); } catch { /* ignore */ }
  }, [isModule, llmMode]);
  useEffect(() => {
    if (!isModule) return;
    try {
      localStorage.setItem(LS_DASHBOARD_OLLAMA_URL, ollamaUrl);
      localStorage.setItem(LS_DASHBOARD_OLLAMA_MODEL, ollamaModel);
    } catch { /* ignore */ }
  }, [isModule, ollamaUrl, ollamaModel]);

  // Probe Ollama when switching to local
  useEffect(() => {
    if (!isModule || llmMode !== "local" || !open) return;
    (async () => setLocalProbe(await probeLocalOllama(ollamaUrl)))();
  }, [isModule, llmMode, ollamaUrl, open]);

  // Fetch action count on open
  useEffect(() => {
    if (open) {
      fetch(`${API_URL}/api/user/actions-count`, { credentials: "include" as RequestCredentials })
        .then((r) => r.ok ? r.json() : null)
        .then((d) => {
          if (d) {
            setActionCount(d.count);
            setMaxFreeActions(d.max_free);
          }
        })
        .catch(() => {});
    }
  }, [open]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, actionsMap]);

  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [open]);

  const handleValidateAction = useCallback((msgIdx: number, actionId: string) => {
    setActionsMap((prev) => ({
      ...prev,
      [msgIdx]: (prev[msgIdx] || []).map((a) =>
        a.id === actionId ? { ...a, validated: !a.validated } : a
      ),
    }));
  }, []);

  const handlePriorityChange = useCallback(
    (msgIdx: number, actionId: string, priority: ActionProposal["priority"]) => {
      setActionsMap((prev) => ({
        ...prev,
        [msgIdx]: (prev[msgIdx] || []).map((a) =>
          a.id === actionId ? { ...a, priority } : a
        ),
      }));
    },
    []
  );

  const sendToBackend = async (text: string) => {
    // Module variant + Local Ollama: route through browser → localhost
    if (isModule && llmMode === "local") {
      const ctxRes = await fetch(`${API_URL}/api/chat/${variant}/context`, { credentials: "include" as RequestCredentials });
      if (!ctxRes.ok) throw new Error("Impossible de récupérer le contexte");
      const ctx = await ctxRes.json();

      const historyMsgs = messages.map((m) => ({ role: m.role, content: m.content }));
      const systemFull = `${ctx.system}\n\n📊 DONNÉES RÉELLES (utilise-les) :\n${JSON.stringify(ctx.context, null, 2)}`;

      const r = await fetch(`${ollamaUrl}/api/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: ollamaModel,
          messages: [
            { role: "system", content: systemFull },
            ...historyMsgs,
            { role: "user", content: text },
          ],
          stream: false,
        }),
      });
      if (!r.ok) throw new Error(`Ollama HTTP ${r.status}`);
      const d = await r.json();
      return { response: d?.message?.content || "", session_id: sessionId || `local-${variant}` };
    }

    // Module Cloud or generic mode: backend call
    const endpoint = isModule ? `${API_URL}/api/chat/${variant}` : `${API_URL}/api/chat`;
    const body = isModule
      ? { message: text, session_id: sessionId }
      : { message: text, module: "orchestrator", session_id: sessionId };

    const resp = await fetch(endpoint, {
      method: "POST",
      credentials: "include" as RequestCredentials,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });

    if (!resp.ok) {
      if (resp.status === 401) {
        toast.error("Session expiree. Veuillez vous reconnecter.");
        return null;
      }
      throw new Error("Erreur API");
    }

    const data = await resp.json();
    const responseText = data.reply ?? data.response ?? "";
    const newSid = data.session_id;
    if (newSid && newSid !== sessionId) {
      setSessionId(newSid);
      try {
        localStorage.setItem(
          isModule ? `grafton_${variant}_chat_session_id` : "grafton_chat_session_id",
          newSid
        );
      } catch { /* ignore */ }
    }
    return { response: responseText, session_id: newSid };
  };

  const send = async (text: string) => {
    if (!text.trim() || isLoading) return;

    // Paywall check
    if (actionCount >= maxFreeActions) {
      setShowPaywall(true);
      return;
    }

    const userMsg: Msg = { role: "user", content: text.trim() };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setIsLoading(true);
    setActionCount((c) => c + 1);

    try {
      const data = await sendToBackend(text.trim());
      if (!data) return;

      const assistantContent = data.response;
      const actions = parseActionsFromResponse(assistantContent);
      const navCommand = parseNavigationFromResponse(assistantContent);
      const cleanContent = cleanContentFromActions(assistantContent);

      const assistantMsg: Msg = { role: "assistant", content: cleanContent };
      setMessages((prev) => [...prev, assistantMsg]);

      if (actions.length > 0) {
        setActionsMap((prev) => {
          const msgIdx = messages.length + 1;
          return { ...prev, [msgIdx]: actions };
        });
      }

      if (navCommand && onNavigate) {
        setTimeout(() => {
          onNavigate(navCommand.route);
          toast.success(`Navigation vers ${navCommand.label}`);
        }, 1500);
      }
    } catch {
      toast.error("Erreur de connexion avec Grafton IA.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send(input);
    }
  };

  return createPortal(
    <>
      {/* Floating button */}
      {!open && (
        <button
          data-testid="grafton-chat-open-btn"
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-[9999] h-14 w-14 rounded-full bg-primary text-primary-foreground shadow-[0_4px_24px_rgba(200,150,50,0.3)] hover:shadow-[0_4px_32px_rgba(200,150,50,0.5)] flex items-center justify-center transition-all duration-200 hover:scale-105 active:scale-95"
          aria-label="Ouvrir Grafton IA"
        >
          <MessageSquare className="h-6 w-6" />
        </button>
      )}

      {/* Overlay */}
      {open && (
        <div
          className="fixed inset-0 z-[9998] bg-black/50 transition-opacity"
          onClick={() => setOpen(false)}
        />
      )}

      {/* Chat Panel */}
      <div
        data-testid="grafton-chat-panel"
        className={`fixed top-0 right-0 z-[9999] h-full w-full sm:w-[440px] bg-background border-l border-border shadow-2xl flex flex-col transition-transform duration-300 ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Header */}
        <div className="px-4 py-3 border-b border-border/50 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-primary/15 flex items-center justify-center">
              <Sparkles className="h-4 w-4 text-primary" />
            </div>
            <div>
              <p className="text-sm font-semibold">Grafton IA</p>
              <p className="text-xs text-muted-foreground">{chatSubtitle}</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            {isModule && (
              <div className="flex items-center gap-0.5 rounded-lg border border-border/50 p-0.5" data-testid="dashboard-chat-mode-toggle">
                <button
                  data-testid="dashboard-chat-mode-cloud"
                  onClick={() => setLlmMode("cloud")}
                  className={`h-6 px-2 rounded-md text-[10px] font-medium flex items-center gap-1 transition-colors ${
                    llmMode === "cloud" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary"
                  }`}
                  title="Mode cloud (DeepSeek)"
                >
                  <Cloud className="h-3 w-3" /> Cloud
                </button>
                <button
                  data-testid="dashboard-chat-mode-local"
                  onClick={() => setLlmMode("local")}
                  className={`h-6 px-2 rounded-md text-[10px] font-medium flex items-center gap-1 transition-colors ${
                    llmMode === "local" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary"
                  }`}
                  title={`Ollama local (${ollamaModel}) — 100% privé`}
                >
                  <HardDrive className="h-3 w-3" /> Local
                </button>
              </div>
            )}
            <button
              onClick={() => setOpen(false)}
              className="h-8 w-8 rounded-lg hover:bg-secondary flex items-center justify-center transition-colors"
              aria-label="Fermer"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Local mode status banner */}
        {isModule && llmMode === "local" && localProbe && (
          <div className={`px-4 py-1.5 text-[10px] flex items-center gap-2 border-b border-border/30 ${
            localProbe.reachable ? "bg-emerald-500/10 text-emerald-400" : "bg-rose-500/10 text-rose-400"
          }`} data-testid="dashboard-chat-local-status">
            {localProbe.reachable ? (
              <>
                <CheckCircle2 className="h-3 w-3" />
                <span>🔒 Mode local actif — {ollamaModel} joignable. Zéro donnée ne quitte ta machine.</span>
              </>
            ) : (
              <>
                <AlertCircle className="h-3 w-3" />
                <span>Ollama injoignable sur {ollamaUrl}. Lance <code className="bg-background/40 px-1 rounded">OLLAMA_ORIGINS=* ollama serve</code>.</span>
              </>
            )}
          </div>
        )}

        {/* Messages */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full gap-4 text-center">
              <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <Sparkles className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="font-medium text-sm">Que puis-je faire pour vous ?</p>
                <p className="text-xs text-muted-foreground mt-1">{chatWelcome}</p>
              </div>
              <div className="grid grid-cols-1 gap-2 w-full max-w-[300px]">
                {QUICK_PROMPTS.map((prompt) => (
                  <button
                    key={prompt}
                    onClick={() => send(prompt)}
                    className="text-xs text-left px-3 py-2 rounded-lg bg-secondary/50 border border-border/50 hover:border-primary/30 hover:bg-secondary transition-colors"
                  >
                    {prompt}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            messages.map((msg, i) => (
              <ChatMessage
                key={i}
                msg={msg}
                actions={actionsMap[i]}
                onValidateAction={(actionId) => handleValidateAction(i, actionId)}
                onPriorityChange={(actionId, priority) => handlePriorityChange(i, actionId, priority)}
              />
            ))
          )}
          {isLoading && messages[messages.length - 1]?.role !== "assistant" && (
            <div className="flex gap-2.5">
              <div className="h-7 w-7 rounded-lg bg-primary/15 flex items-center justify-center shrink-0">
                <Sparkles className="h-3.5 w-3.5 text-primary" />
              </div>
              <div className="bg-secondary/60 border border-border/50 rounded-xl px-3.5 py-2.5">
                <ThinkingPulse />
              </div>
            </div>
          )}
        </div>

        {/* Input */}
        <div className="p-3 border-t border-border/50">
          {!isFounder && actionCount >= maxFreeActions ? (
            <div className="text-center py-2 space-y-2">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20 text-primary text-xs font-semibold">
                <Lock className="h-3 w-3" /> {maxFreeActions} modifications gratuites utilisées
              </div>
              <Button
                variant="hero"
                className="w-full h-10"
                onClick={() => setShowPaywall(true)}
                data-testid="paywall-trigger-btn"
              >
                <Crown className="h-4 w-4 mr-2" /> Passer à Grafton Pro
              </Button>
            </div>
          ) : (
            <>
              <div className="flex items-end gap-2">
                <textarea
                  ref={inputRef}
                  data-testid="grafton-chat-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Posez votre question..."
                  rows={1}
                  className="flex-1 resize-none bg-secondary/50 border border-border/50 rounded-xl px-3.5 py-2.5 text-sm placeholder:text-muted-foreground focus:outline-none focus:border-primary/40 transition-colors"
                  style={{ maxHeight: "120px" }}
                />
                <Button
                  data-testid="grafton-chat-send-btn"
                  size="icon"
                  className="h-10 w-10 rounded-xl shrink-0"
                  onClick={() => send(input)}
                  disabled={!input.trim() || isLoading}
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
              {actionCount > 0 && (
                <div className="flex items-center justify-center gap-2 mt-2">
                  <div className="flex gap-0.5">
                    {Array.from({ length: maxFreeActions }).map((_, i) => (
                      <div
                        key={i}
                        className={`h-1 w-2 rounded-full transition-colors ${
                          i < actionCount ? "bg-primary" : "bg-secondary/60"
                        }`}
                      />
                    ))}
                  </div>
                  <span className="text-[10px] text-muted-foreground">
                    {maxFreeActions - actionCount} modifications restantes
                  </span>
                </div>
              )}
            </>
          )}
        </div>

        <PaywallDialog
          open={showPaywall}
          onOpenChange={setShowPaywall}
          usedCredits={actionCount}
          maxCredits={maxFreeActions}
        />
      </div>
    </>,
    document.body
  );
};
